import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import PageHero from '../components/PageHero';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
};

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      // Track contact form submission in GA
      if (window.gtag) {
        window.gtag('event', 'form_submit', {
          event_category: 'conversion',
          event_label: 'contact_form'
        });
      }
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <PageHero 
        badge="Get In Touch"
        title="Contact"
        titleAccent="Us"
        subtitle="Have questions? We're here to help. Reach out anytime!"
      />

      {/* Contact Section */}
      <section className="bg-white py-20 md:py-32" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                Visit Super Spray Car Wash II
              </h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-1">Address</h3>
                    <p className="text-slate-600">1581 S Muskogee Ave<br />Tahlequah, OK 74464</p>
                    <a 
                      href="https://maps.app.goo.gl/pzyefhoDAQCvaCp89"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1E3A8A] font-medium hover:text-[#DC2626] mt-2 inline-block"
                      data-testid="contact-directions-link"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-1">Phone</h3>
                    <a 
                      href="tel:9182079463"
                      className="text-slate-600 hover:text-[#1E3A8A]"
                      data-testid="contact-phone-link"
                    >
                      918-207-9463
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-1">Email</h3>
                    <a 
                      href="mailto:superspray2@icloud.com"
                      className="text-slate-600 hover:text-[#1E3A8A]"
                      data-testid="contact-email-link"
                    >
                      superspray2@icloud.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-1">Hours</h3>
                    <p className="text-slate-600">Open Daily: 9 AM - 7 PM</p>
                    <p className="text-slate-500 text-sm mt-1">Including weekends & holidays</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="h-64 rounded-2xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3234.5!2d-94.97!3d35.91!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c91c2d5a4a0001%3A0x1234567890abcdef!2s1581%20S%20Muskogee%20Ave%2C%20Tahlequah%2C%20OK%2074464!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Super Spray Car Wash II Location"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp}>
              <div className="bg-slate-50 rounded-2xl p-8 md:p-10">
                <h2 className="text-2xl font-bold text-[#0F172A] mb-6">
                  Send Us a Message
                </h2>

                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-2">Message Sent!</h3>
                    <p className="text-slate-600">
                      Thank you for reaching out. We'll get back to you soon!
                    </p>
                    <Button 
                      onClick={() => setSubmitted(false)}
                      className="mt-6 bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white rounded-full"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="w-full border-slate-300 focus:border-[#1E3A8A] focus:ring-[#1E3A8A] rounded-lg"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full border-slate-300 focus:border-[#1E3A8A] focus:ring-[#1E3A8A] rounded-lg"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Phone (optional)
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full border-slate-300 focus:border-[#1E3A8A] focus:ring-[#1E3A8A] rounded-lg"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        className="w-full border-slate-300 focus:border-[#1E3A8A] focus:ring-[#1E3A8A] resize-none rounded-lg"
                        data-testid="contact-message-input"
                      />
                    </div>

                    {error && (
                      <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm" data-testid="contact-error">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold py-3 rounded-full btn-primary"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Send className="w-5 h-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-slate-50 py-20 md:py-24" data-testid="contact-areas">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6">
              Serving the Greater Tahlequah Area
            </h2>
            <p className="text-slate-600 mb-8">
              We're proud to serve customers from Tahlequah and the surrounding communities.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                'Tahlequah', 'Fort Gibson', 'Muskogee', 'Wagoner', 'Park Hill', 
                'Keys', 'Hubert', 'Locust Grove', 'Cookson', 'Cherokee County'
              ].map((area) => (
                <span 
                  key={area}
                  className="bg-white px-4 py-2 rounded-full text-slate-600 text-sm border border-slate-200"
                >
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
